﻿import { useEffect, useMemo, useState } from "react"

import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Switch,
  TablePagination,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"

import { DataGrid } from "@mui/x-data-grid"

import MainLayout from "../../layouts/MainLayout"

import {
  createUser,
  deleteUser,
  getUserById,
  getUserFormData,
  getUsers,
  updateUser,
} from "../../services/userService"

import { getBusinessLabel } from "../../config/appConfig"


// =========================================================
// BUSINESS LABELS
// =========================================================

const MAIN_COMPANY = getBusinessLabel(
  "mainCompany",
  "Entreprise principale"
)

const MAIN_DISTRIBUTOR = getBusinessLabel(
  "mainDistributor",
  "Distributeur principal"
)

const RELAY_DEPOT = getBusinessLabel(
  "relayDepot",
  "Structure secondaire"
)

const SHOP = getBusinessLabel(
  "shop",
  "Shop"
)

const WAREHOUSE = getBusinessLabel(
  "warehouse",
  "Entrepôt"
)

const FIELD_AGENT = getBusinessLabel(
  "fieldAgent",
  "Agent commercial"
)

const FIELD_AGENTS = getBusinessLabel(
  "fieldAgents",
  "Agents commerciaux"
)


// =========================================================
// ORGANIZATION TYPES
// =========================================================

const CENTRAL_ORGANIZATION_TYPES = [
  "kin_mousse",
  "central",
  "main_company",
]

const DISTRIBUTOR_ORGANIZATION_TYPES = [
  "distributor",
]

const RELAY_ORGANIZATION_TYPES = [
  "relay_depot",
]

const COMMERCIAL_TEAM_ORGANIZATION_TYPES = [
  "commercial_team",
]


// =========================================================
// ROLES
// =========================================================

const STORE_MANAGER_ROLES = [
  "gerant_magasin",
  "gerant_depot_relais",
]

const COMMERCIAL_AGENT_ROLE =
  "agent_commercial"


const CENTRAL_ROLES = [
  "platform_admin",
  "super_admin",
  "dg",
  "gestionnaire_operations",
  "gestionnaire_stock",
  "caissier_central",
  "controleur_stock",
  "viewer",
]


const DISTRIBUTOR_ROLES = [
  "admin_maison_djo",
  "gestionnaire_maison_djo",
  "stock_maison_djo",
  "livreur_maison_djo",
  "gerant_magasin",
]


const RELAY_ROLES = [
  "admin_depot_relais",
  "gestionnaire_depot_relais",
  "stock_depot_relais",
  "gerant_depot_relais",
]


const DISTRIBUTOR_ADMIN_ALLOWED_ROLES = [
  "gestionnaire_maison_djo",
  "stock_maison_djo",
  "livreur_maison_djo",
  "gerant_magasin",
]


const RELAY_ADMIN_ALLOWED_ROLES = [
  "gestionnaire_depot_relais",
  "stock_depot_relais",
  "gerant_depot_relais",
]


// =========================================================
// DEFAULT FORM
// =========================================================

const defaultFormData = {
  company_id: 1,
  organization_id: "",
  full_name: "",
  email: "",
  phone: "",
  password_hash: "",
  role_id: "",
  store_id: "",
  warehouse_id: "",
  is_active: true,
}


// =========================================================
// HELPERS
// =========================================================

function normalizeText(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
}


function normalizeId(value) {
  if (
    value === "" ||
    value === null ||
    value === undefined
  ) {
    return ""
  }

  return Number(value)
}


function getCurrentUserContext() {
  return {
    id:
      localStorage.getItem(
        "kongosys_user_id"
      ) || "",

    role:
      localStorage.getItem(
        "kongosys_role"
      ) || "",

    organizationId:
      localStorage.getItem(
        "kongosys_organization_id"
      ) || "",

    organizationCode:
      localStorage.getItem(
        "kongosys_organization_code"
      ) || "",

    organizationName:
      localStorage.getItem(
        "kongosys_organization_name"
      ) || "",

    organizationType:
      localStorage.getItem(
        "kongosys_organization_type"
      ) || "",
  }
}


function organizationTypeIs(
  organization,
  allowedTypes = []
) {
  return allowedTypes.includes(
    normalizeText(
      organization?.organization_type
    )
  )
}


function getRoleLabel(roleName) {
  const labels = {
    platform_admin:
      "Platform Admin",

    super_admin:
      "Super Admin",

    dg:
      "Direction Générale",

    gestionnaire_operations:
      "Gestionnaire Opérations",

    gestionnaire_stock:
      "Gestionnaire Stock",

    caissier_central:
      "Caissier Central",

    controleur_stock:
      "Contrôleur Stock",

    viewer:
      "Lecture seule",

    agent_commercial:
      FIELD_AGENT,

    admin_maison_djo:
      `Admin ${MAIN_DISTRIBUTOR}`,

    gestionnaire_maison_djo:
      `Gestionnaire ${MAIN_DISTRIBUTOR}`,

    stock_maison_djo:
      `Stock ${MAIN_DISTRIBUTOR}`,

    livreur_maison_djo:
      `Livreur ${MAIN_DISTRIBUTOR}`,

    gerant_magasin:
      `Gérant ${MAIN_DISTRIBUTOR}`,

    admin_depot_relais:
      `Admin ${RELAY_DEPOT}`,

    gestionnaire_depot_relais:
      `Gestionnaire ${RELAY_DEPOT}`,

    stock_depot_relais:
      `Stock ${RELAY_DEPOT}`,

    gerant_depot_relais:
      `Gérant ${RELAY_DEPOT}`,
  }

  return (
    labels[roleName] ||
    roleName ||
    "-"
  )
}


function getOrganizationTypeLabel(type) {
  const value =
    normalizeText(type)

  const labels = {
    kin_mousse:
      "Organisation centrale",

    central:
      "Organisation centrale",

    main_company:
      "Organisation centrale",

    distributor:
      MAIN_DISTRIBUTOR,

    relay_depot:
      RELAY_DEPOT,

    commercial_team:
      FIELD_AGENTS,
  }

  return (
    labels[value] ||
    type ||
    "-"
  )
}


function getOrganizationLabel(
  organization
) {
  if (!organization) {
    return "-"
  }

  return organization.code
    ? `${organization.code} - ${organization.name}`
    : organization.name || "-"
}


function getUserOrganizationLabel(
  row
) {
  if (!row?.organization_name) {
    return "-"
  }

  return row.organization_code
    ? `${row.organization_code} - ${row.organization_name}`
    : row.organization_name
}


function getAssignmentLabel(row) {
  if (row?.store_name) {
    return {
      type: SHOP,
      name: row.store_name,
      code: row.store_code || "",
    }
  }

  if (row?.warehouse_name) {
    return {
      type: WAREHOUSE,
      name: row.warehouse_name,
      code: row.warehouse_code || "",
    }
  }

  return null
}


// =========================================================
// PAGE
// =========================================================

function UsersPage() {
  const theme = useTheme()

  const isMdDown =
    useMediaQuery(
      theme.breakpoints.down("md")
    )

  const isSmDown =
    useMediaQuery(
      theme.breakpoints.down("sm")
    )


  // =======================================================
  // CURRENT USER
  // =======================================================

  const currentUser =
    getCurrentUserContext()

  const currentRole =
    normalizeText(
      currentUser.role
    )

  const currentOrganizationId =
    normalizeId(
      currentUser.organizationId
    )


  // =======================================================
  // DATA
  // =======================================================

  const [
    users,
    setUsers,
  ] = useState([])

  const [
    roles,
    setRoles,
  ] = useState([])

  const [
    organizations,
    setOrganizations,
  ] = useState([])

  const [
    stores,
    setStores,
  ] = useState([])

  const [
    warehouses,
    setWarehouses,
  ] = useState([])

  const [
    companies,
    setCompanies,
  ] = useState([])


  // =======================================================
  // PERMISSIONS
  // =======================================================

  const [
    permissions,
    setPermissions,
  ] = useState({
    can_create: false,
    can_update: false,
    can_delete: false,
    role: currentRole,
  })


  // =======================================================
  // UI STATE
  // =======================================================

  const [
    loading,
    setLoading,
  ] = useState(false)

  const [
    saving,
    setSaving,
  ] = useState(false)

  const [
    open,
    setOpen,
  ] = useState(false)

  const [
    selectedUser,
    setSelectedUser,
  ] = useState(null)

  const [
    formData,
    setFormData,
  ] = useState({
    ...defaultFormData,
  })


  // =======================================================
  // FILTERS
  // =======================================================

  const [
    search,
    setSearch,
  ] = useState("")

  const [
    roleFilter,
    setRoleFilter,
  ] = useState("")

  const [
    organizationFilter,
    setOrganizationFilter,
  ] = useState("")

  const [
    statusFilter,
    setStatusFilter,
  ] = useState("")


  // =======================================================
  // PAGINATION
  // =======================================================

  const [
    paginationModel,
    setPaginationModel,
  ] = useState({
    page: 0,
    pageSize: 10,
  })


  // =======================================================
  // SNACKBAR
  // =======================================================

  const [
    snackbar,
    setSnackbar,
  ] = useState({
    open: false,
    severity: "success",
    message: "",
  })


  const showSnackbar = (
    message,
    severity = "success"
  ) => {
    setSnackbar({
      open: true,
      severity,
      message,
    })
  }


  const handleCloseSnackbar = () => {
    setSnackbar(
      (previous) => ({
        ...previous,
        open: false,
      })
    )
  }


  // =======================================================
  // LOAD USERS
  // =======================================================

  const loadUsers = async () => {
    try {
      setLoading(true)

      const data =
        await getUsers()

      setUsers(
        Array.isArray(data)
          ? data
          : []
      )
    } catch (error) {
      console.error(error)

      showSnackbar(
        error?.response
          ?.data
          ?.detail ||
          "Impossible de charger les utilisateurs",
        "error"
      )
    } finally {
      setLoading(false)
    }
  }


  // =======================================================
  // LOAD FORM DATA
  // =======================================================

  const loadFormData = async () => {
    try {
      const data =
        await getUserFormData()

      setRoles(
        Array.isArray(
          data?.roles
        )
          ? data.roles
          : []
      )

      setOrganizations(
        Array.isArray(
          data?.organizations
        )
          ? data.organizations
          : []
      )

      setStores(
        Array.isArray(
          data?.stores
        )
          ? data.stores
          : []
      )

      setWarehouses(
        Array.isArray(
          data?.warehouses
        )
          ? data.warehouses
          : []
      )

      setCompanies(
        Array.isArray(
          data?.companies
        )
          ? data.companies
          : []
      )

      setPermissions({
        can_create:
          Boolean(
            data?.permissions
              ?.can_create
          ),

        can_update:
          Boolean(
            data?.permissions
              ?.can_update
          ),

        can_delete:
          Boolean(
            data?.permissions
              ?.can_delete
          ),

        can_create_platform_admin:
          Boolean(
            data?.permissions
              ?.can_create_platform_admin
          ),

        can_create_super_admin:
          Boolean(
            data?.permissions
              ?.can_create_super_admin
          ),

        role:
          data?.permissions
            ?.role ||
          currentRole,
      })
    } catch (error) {
      console.error(error)

      showSnackbar(
        error?.response
          ?.data
          ?.detail ||
          "Impossible de charger les données du formulaire",
        "error"
      )
    }
  }


  useEffect(() => {
    loadUsers()
    loadFormData()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  // =======================================================
  // PAGINATION RESET
  // =======================================================

  useEffect(() => {
    setPaginationModel(
      (previous) => ({
        ...previous,
        page: 0,
      })
    )
  }, [
    search,
    roleFilter,
    organizationFilter,
    statusFilter,
  ])


  // =======================================================
  // SELECTED ROLE / ORGANIZATION
  // =======================================================

  const selectedRole =
    useMemo(() => {
      return (
        roles.find(
          (role) =>
            String(role.id) ===
            String(
              formData.role_id
            )
        ) || null
      )
    }, [
      roles,
      formData.role_id,
    ])


  const selectedOrganization =
    useMemo(() => {
      return (
        organizations.find(
          (organization) =>
            String(
              organization.id
            ) ===
            String(
              formData.organization_id
            )
        ) || null
      )
    }, [
      organizations,
      formData.organization_id,
    ])


  const selectedRoleName =
    normalizeText(
      selectedRole?.name
    )

  const selectedOrganizationType =
    normalizeText(
      selectedOrganization
        ?.organization_type
    )


  const isStoreManager =
    STORE_MANAGER_ROLES.includes(
      selectedRoleName
    )

  const isDistributorManager =
    selectedRoleName ===
    "gerant_magasin"

  const isRelayManager =
    selectedRoleName ===
    "gerant_depot_relais"

  const isCommercialAgent =
    selectedRoleName ===
    COMMERCIAL_AGENT_ROLE

  const isCommercialTeam =
    COMMERCIAL_TEAM_ORGANIZATION_TYPES.includes(
      selectedOrganizationType
    )


  // =======================================================
  // RIGHTS
  // =======================================================

  const canCreateUsers =
    Boolean(
      permissions.can_create
    )

  const canUpdateUsers =
    Boolean(
      permissions.can_update
    )

  const canDeleteUsers =
    Boolean(
      permissions.can_delete
    )


  // =======================================================
  // FILTER FORM STORES
  // =======================================================

  const filteredStoresForForm =
    useMemo(() => {
      if (
        !formData.organization_id
      ) {
        return []
      }

      return stores.filter(
        (store) =>
          String(
            store.organization_id
          ) ===
          String(
            formData.organization_id
          )
      )
    }, [
      stores,
      formData.organization_id,
    ])


  // =======================================================
  // FILTER FORM WAREHOUSES
  // =======================================================

  const filteredWarehousesForForm =
    useMemo(() => {
      if (
        !formData.organization_id
      ) {
        return warehouses
      }

      return warehouses.filter(
        (warehouse) =>
          !warehouse.organization_id ||
          String(
            warehouse.organization_id
          ) ===
            String(
              formData.organization_id
            )
      )
    }, [
      warehouses,
      formData.organization_id,
    ])


  // =======================================================
  // RESET FORM
  // =======================================================

  const resetForm = () => {
    setSelectedUser(null)

    const defaultCompanyId =
      companies[0]?.id || 1

    const defaultOrganizationId =
      organizations.length === 1
        ? organizations[0].id
        : ""

    setFormData({
      ...defaultFormData,

      company_id:
        defaultCompanyId,

      organization_id:
        defaultOrganizationId,
    })
  }


  // =======================================================
  // OPEN ADD
  // =======================================================

  const openAddDialog = () => {
    if (!canCreateUsers) {
      showSnackbar(
        "Vous n'avez pas le droit de créer un utilisateur.",
        "warning"
      )

      return
    }

    resetForm()
    setOpen(true)
  }


  // =======================================================
  // ROW PERMISSIONS
  // =======================================================

  const canManageRow = (row) => {
    const rowRole =
      normalizeText(
        row?.role_name
      )

    const rowOrganizationId =
      normalizeId(
        row?.organization_id
      )

    if (
      currentRole ===
      "platform_admin"
    ) {
      return true
    }

    if (
      rowRole ===
      "platform_admin"
    ) {
      return false
    }

    if (
      rowRole ===
      "super_admin"
    ) {
      return false
    }

    if (
      currentRole ===
      "super_admin"
    ) {
      return true
    }

    if (
      currentRole ===
      "admin_maison_djo"
    ) {
      return (
        rowOrganizationId ===
          currentOrganizationId &&
        DISTRIBUTOR_ADMIN_ALLOWED_ROLES.includes(
          rowRole
        )
      )
    }

    if (
      currentRole ===
      "admin_depot_relais"
    ) {
      return (
        rowOrganizationId ===
          currentOrganizationId &&
        RELAY_ADMIN_ALLOWED_ROLES.includes(
          rowRole
        )
      )
    }

    return false
  }


  // =======================================================
  // OPEN EDIT
  // =======================================================

  const openEditDialog =
    async (id) => {
      try {
        const row =
          users.find(
            (item) =>
              Number(item.id) ===
              Number(id)
          )

        if (
          row &&
          !canManageRow(row)
        ) {
          showSnackbar(
            "Vous ne pouvez pas modifier cet utilisateur.",
            "warning"
          )

          return
        }

        const user =
          await getUserById(id)

        setSelectedUser(user)

        setFormData({
          company_id:
            user.company_id ??
            companies[0]?.id ??
            1,

          organization_id:
            user.organization_id ??
            "",

          full_name:
            user.full_name ||
            "",

          email:
            user.email ||
            "",

          phone:
            user.phone ||
            "",

          password_hash:
            user.password_hash ||
            "",

          role_id:
            user.role_id ??
            "",

          store_id:
            user.store_id ??
            "",

          warehouse_id:
            user.warehouse_id ??
            "",

          is_active:
            Boolean(
              user.is_active
            ),
        })

        setOpen(true)
      } catch (error) {
        console.error(error)

        showSnackbar(
          error?.response
            ?.data
            ?.detail ||
            "Impossible de charger l'utilisateur",
          "error"
        )
      }
    }


  // =======================================================
  // CLOSE
  // =======================================================

  const handleClose = () => {
    if (saving) {
      return
    }

    setOpen(false)
    resetForm()
  }


  // =======================================================
  // ORGANIZATION CHANGE
  // =======================================================

  const handleOrganizationChange =
    (event) => {
      const value =
        event.target.value

      setFormData(
        (previous) => ({
          ...previous,

          organization_id:
            value === ""
              ? ""
              : Number(value),

          store_id:
            "",

          warehouse_id:
            "",
        })
      )
    }


  // =======================================================
  // ROLE CHANGE
  // =======================================================

  const handleRoleChange =
    (event) => {
      const value =
        event.target.value

      const role =
        roles.find(
          (item) =>
            String(item.id) ===
            String(value)
        )

      const roleName =
        normalizeText(
          role?.name
        )

      setFormData(
        (previous) => ({
          ...previous,

          role_id:
            value === ""
              ? ""
              : Number(value),

          store_id:
            roleName ===
            COMMERCIAL_AGENT_ROLE
              ? ""
              : previous.store_id,

          warehouse_id:
            roleName ===
              COMMERCIAL_AGENT_ROLE ||
            STORE_MANAGER_ROLES.includes(
              roleName
            )
              ? ""
              : previous.warehouse_id,
        })
      )
    }


  // =======================================================
  // GENERIC CHANGE
  // =======================================================

  const handleChange =
    (event) => {
      const {
        name,
        value,
      } =
        event.target

      if (
        name ===
        "store_id"
      ) {
        setFormData(
          (previous) => ({
            ...previous,

            store_id:
              value === ""
                ? ""
                : Number(value),

            warehouse_id:
              "",
          })
        )

        return
      }

      if (
        name ===
        "warehouse_id"
      ) {
        setFormData(
          (previous) => ({
            ...previous,

            warehouse_id:
              value === ""
                ? ""
                : Number(value),

            store_id:
              "",
          })
        )

        return
      }

      if (
        name ===
        "company_id"
      ) {
        setFormData(
          (previous) => ({
            ...previous,

            company_id:
              value === ""
                ? ""
                : Number(value),
          })
        )

        return
      }

      setFormData(
        (previous) => ({
          ...previous,

          [name]:
            value,
        })
      )
    }


  // =======================================================
  // ACTIVE
  // =======================================================

  const handleToggleActive =
    (event) => {
      setFormData(
        (previous) => ({
          ...previous,

          is_active:
            event.target.checked,
        })
      )
    }


  // =======================================================
  // VALIDATION
  // =======================================================

  const validateForm = () => {
    if (
      !formData.organization_id
    ) {
      showSnackbar(
        "L'organisation est obligatoire.",
        "warning"
      )

      return false
    }

    if (
      !formData.full_name.trim()
    ) {
      showSnackbar(
        "Le nom complet est obligatoire.",
        "warning"
      )

      return false
    }

    if (
      !formData.email.trim()
    ) {
      showSnackbar(
        "L'adresse email est obligatoire.",
        "warning"
      )

      return false
    }

    if (
      !formData.password_hash.trim()
    ) {
      showSnackbar(
        "Le mot de passe est obligatoire.",
        "warning"
      )

      return false
    }

    if (
      !formData.role_id
    ) {
      showSnackbar(
        "Le rôle est obligatoire.",
        "warning"
      )

      return false
    }

    if (
      formData.store_id &&
      formData.warehouse_id
    ) {
      showSnackbar(
        `Choisissez seulement un ${SHOP.toLowerCase()} ou un ${WAREHOUSE.toLowerCase()}.`,
        "warning"
      )

      return false
    }

    if (
      CENTRAL_ROLES.includes(
        selectedRoleName
      ) &&
      !organizationTypeIs(
        selectedOrganization,
        CENTRAL_ORGANIZATION_TYPES
      )
    ) {
      showSnackbar(
        "Ce rôle doit appartenir à l'organisation centrale.",
        "warning"
      )

      return false
    }

    if (
      DISTRIBUTOR_ROLES.includes(
        selectedRoleName
      ) &&
      !organizationTypeIs(
        selectedOrganization,
        DISTRIBUTOR_ORGANIZATION_TYPES
      )
    ) {
      showSnackbar(
        `Ce rôle doit appartenir à une organisation de type ${MAIN_DISTRIBUTOR}.`,
        "warning"
      )

      return false
    }

    if (
      RELAY_ROLES.includes(
        selectedRoleName
      ) &&
      !organizationTypeIs(
        selectedOrganization,
        RELAY_ORGANIZATION_TYPES
      )
    ) {
      showSnackbar(
        `Ce rôle doit appartenir à une organisation de type ${RELAY_DEPOT}.`,
        "warning"
      )

      return false
    }

    if (
      isStoreManager &&
      !formData.store_id
    ) {
      showSnackbar(
        `Un gérant de ${SHOP.toLowerCase()} doit être affecté à un ${SHOP.toLowerCase()}.`,
        "warning"
      )

      return false
    }

    if (
      isDistributorManager
    ) {
      const selectedStore =
        filteredStoresForForm.find(
          (store) =>
            String(store.id) ===
            String(
              formData.store_id
            )
        )

      if (
        selectedStore &&
        !organizationTypeIs(
          selectedStore,
          DISTRIBUTOR_ORGANIZATION_TYPES
        )
      ) {
        showSnackbar(
          `Un gérant ${MAIN_DISTRIBUTOR} doit être lié à un ${SHOP.toLowerCase()} du ${MAIN_DISTRIBUTOR}.`,
          "warning"
        )

        return false
      }
    }

    if (
      isRelayManager
    ) {
      const selectedStore =
        filteredStoresForForm.find(
          (store) =>
            String(store.id) ===
            String(
              formData.store_id
            )
        )

      if (
        selectedStore &&
        !organizationTypeIs(
          selectedStore,
          RELAY_ORGANIZATION_TYPES
        )
      ) {
        showSnackbar(
          `Un gérant ${RELAY_DEPOT} doit être lié à un ${SHOP.toLowerCase()} de ${RELAY_DEPOT}.`,
          "warning"
        )

        return false
      }
    }

    if (
      isCommercialAgent
    ) {
      if (
        !isCommercialTeam
      ) {
        showSnackbar(
          `L'${FIELD_AGENT.toLowerCase()} doit appartenir à une organisation de type ${FIELD_AGENTS}.`,
          "warning"
        )

        return false
      }

      if (
        formData.store_id ||
        formData.warehouse_id
      ) {
        showSnackbar(
          `L'${FIELD_AGENT.toLowerCase()} ne doit pas être lié à un ${SHOP.toLowerCase()} ou à un ${WAREHOUSE.toLowerCase()}.`,
          "warning"
        )

        return false
      }
    }

    return true
  }


  // =======================================================
  // SAVE
  // =======================================================

  const handleSave =
    async () => {
      if (
        !validateForm()
      ) {
        return
      }

      const payload = {
        company_id:
          Number(
            formData.company_id ||
              1
          ),

        organization_id:
          Number(
            formData.organization_id
          ),

        full_name:
          formData.full_name
            .trim(),

        email:
          formData.email
            .trim(),

        phone:
          formData.phone
            ?.trim() ||
          null,

        password_hash:
          formData.password_hash
            .trim(),

        role_id:
          Number(
            formData.role_id
          ),

        store_id:
          formData.store_id ===
          ""
            ? null
            : Number(
                formData.store_id
              ),

        warehouse_id:
          formData.warehouse_id ===
          ""
            ? null
            : Number(
                formData.warehouse_id
              ),

        is_active:
          Boolean(
            formData.is_active
          ),
      }

      try {
        setSaving(true)

        if (
          selectedUser?.id
        ) {
          await updateUser(
            selectedUser.id,
            payload
          )

          showSnackbar(
            "Utilisateur modifié avec succès."
          )
        } else {
          await createUser(
            payload
          )

          showSnackbar(
            "Utilisateur créé avec succès."
          )
        }

        await loadUsers()

        setOpen(false)
        resetForm()
      } catch (error) {
        console.error(error)

        showSnackbar(
          error?.response
            ?.data
            ?.detail ||
            "Impossible d'enregistrer l'utilisateur",
          "error"
        )
      } finally {
        setSaving(false)
      }
    }


  // =======================================================
  // DISABLE
  // =======================================================

  const handleDelete =
    async (row) => {
      if (
        !canManageRow(row)
      ) {
        showSnackbar(
          "Vous ne pouvez pas désactiver cet utilisateur.",
          "warning"
        )

        return
      }

      const confirmed =
        window.confirm(
          `Désactiver le compte de ${row.full_name || row.email} ?`
        )

      if (!confirmed) {
        return
      }

      try {
        await deleteUser(
          row.id
        )

        showSnackbar(
          "Utilisateur désactivé avec succès."
        )

        await loadUsers()
      } catch (error) {
        console.error(error)

        showSnackbar(
          error?.response
            ?.data
            ?.detail ||
            "Impossible de désactiver l'utilisateur",
          "error"
        )
      }
    }


  // =======================================================
  // FILTER USERS
  // =======================================================

  const filteredUsers =
    useMemo(() => {
      const keyword =
        search
          .trim()
          .toLowerCase()

      return users.filter(
        (item) => {
          const matchesSearch =
            keyword === "" ||
            String(
              item.full_name ||
                ""
            )
              .toLowerCase()
              .includes(keyword) ||
            String(
              item.email ||
                ""
            )
              .toLowerCase()
              .includes(keyword) ||
            String(
              item.phone ||
                ""
            )
              .toLowerCase()
              .includes(keyword) ||
            String(
              item.role_name ||
                ""
            )
              .toLowerCase()
              .includes(keyword) ||
            String(
              item.organization_name ||
                ""
            )
              .toLowerCase()
              .includes(keyword) ||
            String(
              item.organization_code ||
                ""
            )
              .toLowerCase()
              .includes(keyword) ||
            String(
              item.store_name ||
                ""
            )
              .toLowerCase()
              .includes(keyword) ||
            String(
              item.warehouse_name ||
                ""
            )
              .toLowerCase()
              .includes(keyword)

          const matchesRole =
            roleFilter === "" ||
            String(
              item.role_id
            ) ===
              String(
                roleFilter
              )

          const matchesOrganization =
            organizationFilter === "" ||
            String(
              item.organization_id
            ) ===
              String(
                organizationFilter
              )

          const matchesStatus =
            statusFilter === "" ||
            (
              statusFilter ===
                "active" &&
              item.is_active
            ) ||
            (
              statusFilter ===
                "inactive" &&
              !item.is_active
            )

          return (
            matchesSearch &&
            matchesRole &&
            matchesOrganization &&
            matchesStatus
          )
        }
      )
    }, [
      users,
      search,
      roleFilter,
      organizationFilter,
      statusFilter,
    ])


  // =======================================================
  // MOBILE PAGINATION
  // =======================================================

  const paginatedUsers =
    useMemo(() => {
      const start =
        paginationModel.page *
        paginationModel.pageSize

      return filteredUsers.slice(
        start,
        start +
          paginationModel.pageSize
      )
    }, [
      filteredUsers,
      paginationModel,
    ])


  useEffect(() => {
    const totalPages =
      Math.max(
        1,
        Math.ceil(
          filteredUsers.length /
            paginationModel.pageSize
        )
      )

    if (
      paginationModel.page >=
      totalPages
    ) {
      setPaginationModel(
        (previous) => ({
          ...previous,
          page:
            totalPages - 1,
        })
      )
    }
  }, [
    filteredUsers.length,
    paginationModel.page,
    paginationModel.pageSize,
  ])


  // =======================================================
  // DESKTOP COLUMNS
  // =======================================================

  const columns =
    useMemo(
      () => [
        {
          field:
            "full_name",

          headerName:
            "Utilisateur",

          minWidth:
            150,

          flex:
            1.05,

          renderCell:
            (params) => (
              <Stack
                spacing={0.1}
                sx={{
                  minWidth: 0,
                  width: "100%",
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={800}
                  noWrap
                  title={
                    params.row
                      .full_name ||
                    ""
                  }
                >
                  {params.row
                    .full_name ||
                    "-"}
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                >
                  ID #{params.row.id}
                </Typography>
              </Stack>
            ),
        },

        {
          field:
            "contact",

          headerName:
            "Contact",

          minWidth:
            170,

          flex:
            1.15,

          sortable:
            false,

          valueGetter: (
            _value,
            row
          ) =>
            row?.email || "",

          renderCell:
            (params) => (
              <Stack
                spacing={0.1}
                sx={{
                  minWidth: 0,
                  width: "100%",
                }}
              >
                <Typography
                  variant="body2"
                  noWrap
                  title={
                    params.row
                      .email ||
                    ""
                  }
                >
                  {params.row
                    .email ||
                    "-"}
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                >
                  {params.row
                    .phone ||
                    "Téléphone non renseigné"}
                </Typography>
              </Stack>
            ),
        },

        {
          field:
            "role_name",

          headerName:
            "Rôle",

          minWidth:
            135,

          flex:
            0.85,

          renderCell:
            (params) => {
              const label =
                getRoleLabel(
                  params.value
                )

              return (
                <Box
                  title={label}
                  sx={{
                    maxWidth:
                      "100%",
                  }}
                >
                  <Chip
                    label={label}
                    size="small"
                    color={
                      params.value ===
                      "platform_admin"
                        ? "secondary"
                        : params.value ===
                          "super_admin"
                        ? "primary"
                        : "default"
                    }
                    sx={{
                      maxWidth:
                        "100%",

                      fontWeight:
                        800,

                      "& .MuiChip-label":
                        {
                          display:
                            "block",

                          overflow:
                            "hidden",

                          textOverflow:
                            "ellipsis",
                        },
                    }}
                  />
                </Box>
              )
            },
        },

        {
          field:
            "organization_name",

          headerName:
            "Organisation",

          minWidth:
            165,

          flex:
            1.1,

          renderCell:
            (params) => (
              <Stack
                spacing={0.1}
                sx={{
                  minWidth: 0,
                  width: "100%",
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={700}
                  noWrap
                  title={
                    params.row
                      .organization_name ||
                    ""
                  }
                >
                  {params.row
                    .organization_name ||
                    "-"}
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  title={
                    getOrganizationTypeLabel(
                      params.row
                        .organization_type
                    )
                  }
                >
                  {params.row
                    .organization_code ||
                    "-"}{" "}
                  •{" "}
                  {getOrganizationTypeLabel(
                    params.row
                      .organization_type
                  )}
                </Typography>
              </Stack>
            ),
        },

        {
          field:
            "assignment",

          headerName:
            "Affectation",

          minWidth:
            125,

          flex:
            0.85,

          sortable:
            false,

          renderCell:
            (params) => {
              const assignment =
                getAssignmentLabel(
                  params.row
                )

              if (!assignment) {
                return (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    —
                  </Typography>
                )
              }

              return (
                <Stack
                  spacing={0.1}
                  sx={{
                    minWidth: 0,
                    width: "100%",
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    noWrap
                    title={
                      assignment.name
                    }
                  >
                    {assignment.name}
                  </Typography>

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                  >
                    {assignment.type}
                    {assignment.code
                      ? ` • ${assignment.code}`
                      : ""}
                  </Typography>
                </Stack>
              )
            },
        },

        {
          field:
            "is_active",

          headerName:
            "Statut",

          width:
            88,

          renderCell:
            (params) => (
              <Chip
                label={
                  params.value
                    ? "Actif"
                    : "Inactif"
                }
                size="small"
                color={
                  params.value
                    ? "success"
                    : "default"
                }
                variant={
                  params.value
                    ? "filled"
                    : "outlined"
                }
                sx={{
                  fontWeight:
                    800,
                }}
              />
            ),
        },

        {
          field:
            "actions",

          headerName:
            "Actions",

          width:
            176,

          sortable:
            false,

          filterable:
            false,

          disableColumnMenu:
            true,

          renderCell:
            (params) => {
              const manageable =
                canManageRow(
                  params.row
                )

              return (
                <Stack
                  direction="row"
                  spacing={0.6}
                  sx={{
                    width:
                      "100%",
                  }}
                >
                  <Button
                    variant="contained"
                    disableElevation
                    size="small"
                    onClick={() =>
                      openEditDialog(
                        params.row.id
                      )
                    }
                    disabled={
                      !manageable ||
                      !canUpdateUsers
                    }
                    sx={{
                      minWidth:
                        72,

                      px:
                        1,
                    }}
                  >
                    Modifier
                  </Button>

                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() =>
                      handleDelete(
                        params.row
                      )
                    }
                    disabled={
                      !manageable ||
                      !canDeleteUsers ||
                      !params.row
                        .is_active
                    }
                    sx={{
                      minWidth:
                        86,

                      px:
                        0.8,
                    }}
                  >
                    Désactiver
                  </Button>
                </Stack>
              )
            },
        },
      ],
      [
        canUpdateUsers,
        canDeleteUsers,
        currentRole,
        currentOrganizationId,
        users,
      ]
    )


  // =======================================================
  // PAGE DESCRIPTION
  // =======================================================

  const pageDescription =
    useMemo(() => {
      if (
        currentRole ===
        "platform_admin"
      ) {
        return "Compte technique : peut créer tous les comptes, y compris Super Admin et Platform Admin."
      }

      if (
        currentRole ===
        "super_admin"
      ) {
        return `${MAIN_COMPANY} : peut créer les comptes métiers, sauf Platform Admin et Super Admin.`
      }

      if (
        currentRole ===
        "admin_maison_djo"
      ) {
        return `${MAIN_DISTRIBUTOR} : crée uniquement les comptes autorisés pour son organisation.`
      }

      if (
        currentRole ===
        "admin_depot_relais"
      ) {
        return `${RELAY_DEPOT} : crée uniquement les comptes autorisés pour sa structure.`
      }

      return "Gestion des utilisateurs."
    }, [
      currentRole,
    ])


  // =======================================================
  // RESET FILTERS
  // =======================================================

  const resetFilters = () => {
    setSearch("")
    setRoleFilter("")
    setOrganizationFilter("")
    setStatusFilter("")

    setPaginationModel({
      page: 0,
      pageSize: 10,
    })
  }


  // =======================================================
  // RENDER
  // =======================================================

  return (
    <MainLayout>
      <Box
        sx={{
          minWidth: 0,

          width: "100%",

          pb: {
            xs: 2,
            md: 4,
          },

          "& .MuiButton-root":
            {
              textTransform:
                "none",

              fontWeight:
                800,

              borderRadius:
                2.5,
            },

          "& .MuiOutlinedInput-root":
            {
              borderRadius:
                2.5,
            },
        }}
      >

        {/* =================================================
            HEADER
        ================================================= */}

        <Box
          sx={{
            display:
              "grid",

            gridTemplateColumns:
              {
                xs:
                  "1fr",

                sm:
                  "minmax(0, 1fr) auto",
              },

            alignItems:
              "start",

            columnGap:
              2,

            rowGap:
              1.25,

            mb:
              2.5,
          }}
        >
          <Box
            sx={{
              minWidth: 0,
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight:
                  900,

                color:
                  "#0f172a",

                lineHeight:
                  1.12,

                letterSpacing:
                  "-0.025em",

                fontSize:
                  {
                    xs:
                      "1.65rem",

                    sm:
                      "1.95rem",

                    md:
                      "2.1rem",
                  },
              }}
            >
              Utilisateurs
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt:
                  0.45,

                lineHeight:
                  1.55,

                maxWidth:
                  760,
              }}
            >
              Gestion des comptes par rôle, organisation,{" "}
              {SHOP.toLowerCase()} ou{" "}
              {WAREHOUSE.toLowerCase()}.
            </Typography>
          </Box>


          <Button
            variant="contained"
            disableElevation
            onClick={
              openAddDialog
            }
            disabled={
              !canCreateUsers
            }
            sx={{
              minHeight:
                42,

              px:
                2.4,

              minWidth:
                {
                  xs:
                    "100%",

                  sm:
                    170,
                },

              justifySelf:
                {
                  xs:
                    "stretch",

                  sm:
                    "end",
                },

              boxShadow:
                "0 8px 18px rgba(37, 99, 235, 0.16)",
            }}
          >
            Ajouter utilisateur
          </Button>
        </Box>


        {/* =================================================
            PERMISSION INFO
        ================================================= */}

        <Alert
          severity="info"
          variant="outlined"
          sx={{
            mb:
              2,

            borderRadius:
              3,

            alignItems:
              "center",

            bgcolor:
              "rgba(14, 165, 233, 0.03)",
          }}
        >
          {pageDescription}
        </Alert>


        {/* =================================================
            FILTERS
        ================================================= */}

        <Paper
          variant="outlined"
          sx={{
            p: {
              xs:
                1.25,

              sm:
                1.5,
            },

            mb:
              2,

            borderRadius:
              3.5,

            borderColor:
              "rgba(148, 163, 184, 0.25)",

            boxShadow:
              "0 8px 24px rgba(15, 23, 42, 0.035)",
          }}
        >
          <Box
            sx={{
              display:
                "grid",

              gridTemplateColumns:
                {
                  xs:
                    "1fr",

                  sm:
                    "repeat(2, minmax(0, 1fr))",

                  lg:
                    "minmax(220px, 1.45fr) minmax(170px, 1fr) minmax(150px, 0.9fr) minmax(125px, 0.7fr) auto",
                },

              gap:
                1.15,

              alignItems:
                "center",
            }}
          >
            <TextField
              fullWidth
              size="small"
              label="Recherche"
              placeholder="Nom, email, rôle..."
              value={
                search
              }
              onChange={(
                event
              ) =>
                setSearch(
                  event.target
                    .value
                )
              }
            />


            <TextField
              select
              fullWidth
              size="small"
              label="Organisation"
              value={
                organizationFilter
              }
              onChange={(
                event
              ) =>
                setOrganizationFilter(
                  event.target
                    .value
                )
              }
            >
              <MenuItem value="">
                Toutes les organisations
              </MenuItem>

              {organizations.map(
                (organization) => (
                  <MenuItem
                    key={
                      organization.id
                    }
                    value={
                      organization.id
                    }
                  >
                    {getOrganizationLabel(
                      organization
                    )}
                  </MenuItem>
                )
              )}
            </TextField>


            <TextField
              select
              fullWidth
              size="small"
              label="Rôle"
              value={
                roleFilter
              }
              onChange={(
                event
              ) =>
                setRoleFilter(
                  event.target
                    .value
                )
              }
            >
              <MenuItem value="">
                Tous les rôles
              </MenuItem>

              {roles.map(
                (role) => (
                  <MenuItem
                    key={
                      role.id
                    }
                    value={
                      role.id
                    }
                  >
                    {getRoleLabel(
                      role.name
                    )}
                  </MenuItem>
                )
              )}
            </TextField>


            <TextField
              select
              fullWidth
              size="small"
              label="Statut"
              value={
                statusFilter
              }
              onChange={(
                event
              ) =>
                setStatusFilter(
                  event.target
                    .value
                )
              }
            >
              <MenuItem value="">
                Tous
              </MenuItem>

              <MenuItem value="active">
                Actif
              </MenuItem>

              <MenuItem value="inactive">
                Inactif
              </MenuItem>
            </TextField>


            <Button
              variant="outlined"
              onClick={
                resetFilters
              }
              sx={{
                minHeight:
                  40,

                minWidth:
                  {
                    xs:
                      "100%",

                    lg:
                      105,
                  },
              }}
            >
              Réinitialiser
            </Button>
          </Box>
        </Paper>


        {/* =================================================
            TABLE CONTAINER
        ================================================= */}

        <Paper
          variant="outlined"
          sx={{
            borderRadius:
              3.5,

            overflow:
              "hidden",

            minWidth:
              0,

            borderColor:
              "rgba(148, 163, 184, 0.25)",

            boxShadow:
              "0 10px 28px rgba(15, 23, 42, 0.04)",
          }}
        >

          {/* HEADER */}

          <Box
            sx={{
              px: {
                xs:
                  1.5,

                sm:
                  2,
              },

              py:
                1.4,

              display:
                "flex",

              flexDirection:
                {
                  xs:
                    "column",

                  sm:
                    "row",
                },

              justifyContent:
                "space-between",

              alignItems:
                {
                  xs:
                    "flex-start",

                  sm:
                    "center",
                },

              gap:
                1,

              borderBottom:
                "1px solid",

              borderColor:
                "divider",
            }}
          >
            <Box>
              <Typography
                fontWeight={900}
                color="#0f172a"
              >
                Liste des utilisateurs
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
              >
                Comptes, rôles et affectations dans RLSys Business
              </Typography>
            </Box>


            <Chip
              size="small"
              variant="outlined"
              label={`${filteredUsers.length} utilisateur${
                filteredUsers.length > 1
                  ? "s"
                  : ""
              }`}
              sx={{
                fontWeight:
                  800,
              }}
            />
          </Box>


          {/* =================================================
              DESKTOP / LAPTOP DATAGRID
          ================================================= */}

          {!isMdDown ? (
            <Box
              sx={{
                width:
                  "100%",

                minWidth:
                  0,

                overflow:
                  "hidden",

                bgcolor:
                  "#ffffff",
              }}
            >
              <DataGrid
                autoHeight
                rows={
                  filteredUsers
                }
                columns={
                  columns
                }
                loading={
                  loading
                }
                disableRowSelectionOnClick
                disableColumnSelector
                pagination
                paginationModel={
                  paginationModel
                }
                onPaginationModelChange={
                  setPaginationModel
                }
                pageSizeOptions={[
                  10,
                ]}
                rowHeight={64}
                columnHeaderHeight={50}
                localeText={{
                  noRowsLabel:
                    "Aucun utilisateur trouvé",

                  noResultsOverlayLabel:
                    "Aucun résultat",

                  MuiTablePagination:
                    {
                      labelRowsPerPage:
                        "Lignes par page",

                      labelDisplayedRows:
                        ({
                          from,
                          to,
                          count,
                        }) =>
                          `${from}–${to} sur ${count}`,
                    },
                }}
                sx={{
                  border:
                    0,

                  width:
                    "100%",

                  minWidth:
                    0,

                  color:
                    "#334155",

                  "& .MuiDataGrid-columnHeaders":
                    {
                      bgcolor:
                        "#f8fafc",

                      borderBottom:
                        "1px solid rgba(148, 163, 184, 0.22)",
                    },

                  "& .MuiDataGrid-columnHeader":
                    {
                      bgcolor:
                        "#f8fafc",

                      px:
                        1,
                    },

                  "& .MuiDataGrid-columnHeaderTitle":
                    {
                      fontWeight:
                        900,

                      color:
                        "#334155",

                      fontSize:
                        "0.79rem",
                    },

                  "& .MuiDataGrid-cell":
                    {
                      borderColor:
                        "rgba(148, 163, 184, 0.14)",

                      display:
                        "flex",

                      alignItems:
                        "center",

                      minWidth:
                        0,

                      px:
                        1,
                    },

                  "& .MuiDataGrid-row:hover":
                    {
                      bgcolor:
                        "#f8fbff",
                    },

                  "& .MuiDataGrid-footerContainer":
                    {
                      minHeight:
                        52,

                      borderTop:
                        "1px solid rgba(148, 163, 184, 0.2)",

                      bgcolor:
                        "#ffffff",
                    },

                  "& .MuiTablePagination-toolbar":
                    {
                      minHeight:
                        52,
                    },

                  "& .MuiDataGrid-scrollbar--horizontal":
                    {
                      display:
                        "none",
                    },

                  "& .MuiDataGrid-virtualScroller":
                    {
                      overflowX:
                        "hidden",
                    },

                  "& .MuiDataGrid-overlay":
                    {
                      bgcolor:
                        "#ffffff",
                    },
                }}
              />
            </Box>
          ) : (

            /* =================================================
               TABLET / MOBILE CARDS
            ================================================= */

            <Box
              sx={{
                p: {
                  xs:
                    1.25,

                  sm:
                    1.5,
                },

                bgcolor:
                  "#f8fafc",
              }}
            >
              {loading ? (
                <Box
                  sx={{
                    py:
                      5,

                    textAlign:
                      "center",
                  }}
                >
                  <Typography
                    color="text.secondary"
                  >
                    Chargement des utilisateurs...
                  </Typography>
                </Box>
              ) : paginatedUsers.length ===
                0 ? (
                <Box
                  sx={{
                    py:
                      5,

                    textAlign:
                      "center",
                  }}
                >
                  <Typography
                    color="text.secondary"
                  >
                    Aucun utilisateur trouvé.
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={1.25}>
                  {paginatedUsers.map(
                    (row) => {
                      const manageable =
                        canManageRow(row)

                      const assignment =
                        getAssignmentLabel(
                          row
                        )

                      return (
                        <Paper
                          key={
                            row.id
                          }
                          variant="outlined"
                          sx={{
                            p: {
                              xs:
                                1.5,

                              sm:
                                1.75,
                            },

                            borderRadius:
                              3,

                            borderColor:
                              "rgba(148, 163, 184, 0.23)",

                            bgcolor:
                              "#ffffff",
                          }}
                        >
                          <Stack spacing={1.4}>

                            {/* CARD TOP */}

                            <Box
                              sx={{
                                display:
                                  "flex",

                                justifyContent:
                                  "space-between",

                                alignItems:
                                  "flex-start",

                                gap:
                                  1.5,
                              }}
                            >
                              <Box
                                sx={{
                                  minWidth:
                                    0,
                                }}
                              >
                                <Typography
                                  fontWeight={900}
                                  color="#0f172a"
                                  noWrap
                                >
                                  {row.full_name ||
                                    "-"}
                                </Typography>

                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  ID #{row.id}
                                </Typography>
                              </Box>


                              <Chip
                                size="small"
                                label={
                                  row.is_active
                                    ? "Actif"
                                    : "Inactif"
                                }
                                color={
                                  row.is_active
                                    ? "success"
                                    : "default"
                                }
                                sx={{
                                  fontWeight:
                                    800,
                                }}
                              />
                            </Box>


                            {/* ROLE */}

                            <Box>
                              <Chip
                                size="small"
                                label={
                                  getRoleLabel(
                                    row.role_name
                                  )
                                }
                                color={
                                  row.role_name ===
                                  "platform_admin"
                                    ? "secondary"
                                    : row.role_name ===
                                      "super_admin"
                                    ? "primary"
                                    : "default"
                                }
                                sx={{
                                  fontWeight:
                                    800,

                                  maxWidth:
                                    "100%",
                                }}
                              />
                            </Box>


                            {/* INFO GRID */}

                            <Box
                              sx={{
                                display:
                                  "grid",

                                gridTemplateColumns:
                                  {
                                    xs:
                                      "1fr",

                                    sm:
                                      "repeat(2, minmax(0, 1fr))",
                                  },

                                gap:
                                  1.25,
                              }}
                            >
                              <Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  fontWeight={700}
                                >
                                  Contact
                                </Typography>

                                <Typography
                                  variant="body2"
                                  fontWeight={700}
                                  sx={{
                                    overflowWrap:
                                      "anywhere",
                                  }}
                                >
                                  {row.email ||
                                    "-"}
                                </Typography>

                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {row.phone ||
                                    "Téléphone non renseigné"}
                                </Typography>
                              </Box>


                              <Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  fontWeight={700}
                                >
                                  Organisation
                                </Typography>

                                <Typography
                                  variant="body2"
                                  fontWeight={700}
                                >
                                  {row.organization_name ||
                                    "-"}
                                </Typography>

                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {row.organization_code ||
                                    "-"}{" "}
                                  •{" "}
                                  {getOrganizationTypeLabel(
                                    row.organization_type
                                  )}
                                </Typography>
                              </Box>


                              <Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  fontWeight={700}
                                >
                                  Affectation
                                </Typography>

                                {assignment ? (
                                  <>
                                    <Typography
                                      variant="body2"
                                      fontWeight={700}
                                    >
                                      {assignment.name}
                                    </Typography>

                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {assignment.type}
                                      {assignment.code
                                        ? ` • ${assignment.code}`
                                        : ""}
                                    </Typography>
                                  </>
                                ) : (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Aucune affectation directe
                                  </Typography>
                                )}
                              </Box>
                            </Box>


                            {/* ACTIONS */}

                            <Box
                              sx={{
                                display:
                                  "grid",

                                gridTemplateColumns:
                                  "repeat(2, minmax(0, 1fr))",

                                gap:
                                  1,
                              }}
                            >
                              <Button
                                variant="contained"
                                disableElevation
                                size="small"
                                onClick={() =>
                                  openEditDialog(
                                    row.id
                                  )
                                }
                                disabled={
                                  !manageable ||
                                  !canUpdateUsers
                                }
                              >
                                Modifier
                              </Button>

                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                onClick={() =>
                                  handleDelete(
                                    row
                                  )
                                }
                                disabled={
                                  !manageable ||
                                  !canDeleteUsers ||
                                  !row.is_active
                                }
                              >
                                Désactiver
                              </Button>
                            </Box>
                          </Stack>
                        </Paper>
                      )
                    }
                  )}
                </Stack>
              )}


              <TablePagination
                component="div"
                count={
                  filteredUsers.length
                }
                page={
                  paginationModel.page
                }
                onPageChange={(
                  _event,
                  newPage
                ) =>
                  setPaginationModel(
                    (previous) => ({
                      ...previous,
                      page:
                        newPage,
                    })
                  )
                }
                rowsPerPage={
                  10
                }
                rowsPerPageOptions={[
                  10,
                ]}
                labelRowsPerPage="Lignes"
                labelDisplayedRows={({
                  from,
                  to,
                  count,
                }) =>
                  `${from}–${to} sur ${count}`
                }
                sx={{
                  mt:
                    1,

                  bgcolor:
                    "#ffffff",

                  borderRadius:
                    2.5,

                  "& .MuiTablePagination-toolbar":
                    {
                      px:
                        1,

                      minHeight:
                        52,
                    },

                  "& .MuiTablePagination-selectLabel":
                    {
                      display:
                        "none",
                    },

                  "& .MuiTablePagination-input":
                    {
                      display:
                        "none",
                    },
                }}
              />
            </Box>
          )}
        </Paper>
      </Box>


      {/* =================================================
          ADD / EDIT DIALOG
      ================================================= */}

      <Dialog
        open={
          open
        }
        onClose={
          handleClose
        }
        fullWidth
        maxWidth="md"
        fullScreen={
          isSmDown
        }
        PaperProps={{
          sx: {
            borderRadius:
              {
                xs:
                  0,

                sm:
                  4,
              },

            maxHeight:
              {
                xs:
                  "100dvh",

                sm:
                  "90dvh",
              },

            m:
              {
                xs:
                  0,

                sm:
                  2,
              },
          },
        }}
      >
        <DialogTitle
          sx={{
            pb:
              1.5,
          }}
        >
          <Typography
            fontSize={20}
            fontWeight={900}
            color="#0f172a"
          >
            {selectedUser
              ? "Modifier utilisateur"
              : "Ajouter utilisateur"}
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt:
                0.35,
            }}
          >
            {selectedUser
              ? "Modifiez les informations, le rôle et les affectations du compte."
              : "Créez un nouveau compte et définissez son organisation, son rôle et ses affectations."}
          </Typography>
        </DialogTitle>


        <DialogContent dividers>
          <Stack
            spacing={2}
            sx={{
              pt:
                0.5,
            }}
          >
            {selectedUser ? (
              <Alert
                severity="info"
                variant="outlined"
                sx={{
                  borderRadius:
                    3,
                }}
              >
                Modification de :{" "}
                <strong>
                  {selectedUser.full_name}
                </strong>{" "}
                —{" "}
                {getUserOrganizationLabel(
                  selectedUser
                )}
              </Alert>
            ) : null}


            <Box
              sx={{
                display:
                  "grid",

                gridTemplateColumns:
                  {
                    xs:
                      "1fr",

                    md:
                      "repeat(2, minmax(0, 1fr))",
                  },

                gap:
                  2,
              }}
            >
              <TextField
                select
                size="small"
                label="Organisation"
                name="organization_id"
                value={
                  formData.organization_id
                }
                onChange={
                  handleOrganizationChange
                }
                fullWidth
                required
                disabled={
                  saving
                }
                helperText={
                  selectedOrganization
                    ? `Type : ${getOrganizationTypeLabel(
                        selectedOrganization.organization_type
                      )}`
                    : "Sélectionnez l'organisation du compte"
                }
              >
                {organizations.map(
                  (organization) => (
                    <MenuItem
                      key={
                        organization.id
                      }
                      value={
                        organization.id
                      }
                    >
                      {getOrganizationLabel(
                        organization
                      )}{" "}
                      —{" "}
                      {getOrganizationTypeLabel(
                        organization.organization_type
                      )}
                    </MenuItem>
                  )
                )}
              </TextField>


              <TextField
                select
                size="small"
                label="Rôle"
                name="role_id"
                value={
                  formData.role_id
                }
                onChange={
                  handleRoleChange
                }
                fullWidth
                required
                disabled={
                  saving
                }
              >
                {roles.map(
                  (role) => (
                    <MenuItem
                      key={
                        role.id
                      }
                      value={
                        role.id
                      }
                    >
                      {getRoleLabel(
                        role.name
                      )}
                    </MenuItem>
                  )
                )}
              </TextField>
            </Box>


            <Box
              sx={{
                display:
                  "grid",

                gridTemplateColumns:
                  {
                    xs:
                      "1fr",

                    md:
                      "repeat(2, minmax(0, 1fr))",
                  },

                gap:
                  2,
              }}
            >
              <TextField
                size="small"
                label="Nom complet"
                name="full_name"
                value={
                  formData.full_name
                }
                onChange={
                  handleChange
                }
                fullWidth
                required
                disabled={
                  saving
                }
              />

              <TextField
                size="small"
                type="email"
                label="Email"
                name="email"
                value={
                  formData.email
                }
                onChange={
                  handleChange
                }
                fullWidth
                required
                disabled={
                  saving
                }
              />
            </Box>


            <Box
              sx={{
                display:
                  "grid",

                gridTemplateColumns:
                  {
                    xs:
                      "1fr",

                    md:
                      "repeat(2, minmax(0, 1fr))",
                  },

                gap:
                  2,
              }}
            >
              <TextField
                size="small"
                label="Téléphone"
                name="phone"
                value={
                  formData.phone
                }
                onChange={
                  handleChange
                }
                fullWidth
                disabled={
                  saving
                }
              />

              <TextField
                size="small"
                label="Mot de passe"
                name="password_hash"
                value={
                  formData.password_hash
                }
                onChange={
                  handleChange
                }
                fullWidth
                required
                disabled={
                  saving
                }
                helperText="Pour le prototype, ce champ utilise encore le mécanisme actuel du backend."
              />
            </Box>


            <Box
              sx={{
                display:
                  "grid",

                gridTemplateColumns:
                  {
                    xs:
                      "1fr",

                    md:
                      "repeat(2, minmax(0, 1fr))",
                  },

                gap:
                  2,
              }}
            >
              <TextField
                select
                size="small"
                label={
                  SHOP
                }
                name="store_id"
                value={
                  formData.store_id
                }
                onChange={
                  handleChange
                }
                fullWidth
                disabled={
                  saving ||
                  !formData.organization_id ||
                  isCommercialTeam ||
                  isCommercialAgent
                }
                helperText={
                  isDistributorManager
                    ? `Obligatoire pour un gérant ${MAIN_DISTRIBUTOR}`
                    : isRelayManager
                    ? `Obligatoire pour un gérant ${RELAY_DEPOT}`
                    : isCommercialTeam ||
                      isCommercialAgent
                    ? `${FIELD_AGENTS} : pas de liaison directe avec un ${SHOP.toLowerCase()}`
                    : "Optionnel selon le rôle"
                }
              >
                <MenuItem value="">
                  Aucun
                </MenuItem>

                {filteredStoresForForm.map(
                  (store) => (
                    <MenuItem
                      key={
                        store.id
                      }
                      value={
                        store.id
                      }
                    >
                      {store.code} -{" "}
                      {store.name}
                    </MenuItem>
                  )
                )}
              </TextField>


              <TextField
                select
                size="small"
                label={
                  WAREHOUSE
                }
                name="warehouse_id"
                value={
                  formData.warehouse_id
                }
                onChange={
                  handleChange
                }
                fullWidth
                disabled={
                  saving ||
                  isStoreManager ||
                  isCommercialAgent
                }
                helperText={
                  isStoreManager
                    ? `Un gérant ${SHOP.toLowerCase()} ne doit pas être lié à un ${WAREHOUSE.toLowerCase()}`
                    : isCommercialAgent
                    ? `Un ${FIELD_AGENT.toLowerCase()} ne doit pas être lié à un ${WAREHOUSE.toLowerCase()}`
                    : "Optionnel"
                }
              >
                <MenuItem value="">
                  Aucun
                </MenuItem>

                {filteredWarehousesForForm.map(
                  (warehouse) => (
                    <MenuItem
                      key={
                        warehouse.id
                      }
                      value={
                        warehouse.id
                      }
                    >
                      {warehouse.code} -{" "}
                      {warehouse.name}
                    </MenuItem>
                  )
                )}
              </TextField>
            </Box>


            <Box
              sx={{
                display:
                  "grid",

                gridTemplateColumns:
                  {
                    xs:
                      "1fr",

                    md:
                      "minmax(0, 1fr) auto",
                  },

                gap:
                  2,

                alignItems:
                  "center",
              }}
            >
              <TextField
                select
                size="small"
                label="Entreprise"
                name="company_id"
                value={
                  formData.company_id
                }
                onChange={
                  handleChange
                }
                fullWidth
                disabled={
                  saving
                }
              >
                {companies.map(
                  (company) => (
                    <MenuItem
                      key={
                        company.id
                      }
                      value={
                        company.id
                      }
                    >
                      {company.name}
                    </MenuItem>
                  )
                )}
              </TextField>


              <Paper
                variant="outlined"
                sx={{
                  px:
                    1.5,

                  py:
                    0.35,

                  borderRadius:
                    2.5,

                  bgcolor:
                    "#f8fafc",
                }}
              >
                <FormControlLabel
                  sx={{
                    m:
                      0,
                  }}
                  control={
                    <Switch
                      checked={
                        Boolean(
                          formData.is_active
                        )
                      }
                      onChange={
                        handleToggleActive
                      }
                      disabled={
                        saving
                      }
                    />
                  }
                  label={
                    formData.is_active
                      ? "Compte actif"
                      : "Compte inactif"
                  }
                />
              </Paper>
            </Box>
          </Stack>
        </DialogContent>


        <DialogActions
          sx={{
            px: {
              xs:
                2,

              sm:
                3,
            },

            py:
              2,

            gap:
              1,

            borderTop:
              "1px solid",

            borderColor:
              "divider",
          }}
        >
          <Button
            onClick={
              handleClose
            }
            disabled={
              saving
            }
          >
            Annuler
          </Button>

          <Button
            variant="contained"
            disableElevation
            onClick={
              handleSave
            }
            disabled={
              saving
            }
          >
            {saving
              ? "Enregistrement..."
              : selectedUser
              ? "Modifier"
              : "Créer"}
          </Button>
        </DialogActions>
      </Dialog>


      {/* =================================================
          SNACKBAR
      ================================================= */}

      <Snackbar
        open={
          snackbar.open
        }
        autoHideDuration={
          3500
        }
        onClose={
          handleCloseSnackbar
        }
        anchorOrigin={{
          vertical:
            "top",

          horizontal:
            "right",
        }}
      >
        <Alert
          onClose={
            handleCloseSnackbar
          }
          severity={
            snackbar.severity
          }
          variant="filled"
          sx={{
            width:
              "100%",

            borderRadius:
              3,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MainLayout>
  )
}


export default UsersPage